import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export type CompanyInterviewArticleListType = {
  id: number;
  title: string | null;
  is_public: boolean;
};

export type GetCompanyInterviewArticleDetailType = {
  id: number;
  slug: string | null;
  microcms_content_id: string | null;
  microcms_edit_url: string | null;
  title: string | null;
  subheading: string | null;
  banner_image_url: string | null;
  content: string | null;
  interviewer_atmosphere: string | null;
  is_public: boolean;
};

export const useInterviewArticles = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const authHeaders = {
    uid: cookies['uid'],
    client: cookies['client'],
    'access-token': cookies['access-token'],
  };

  const getCompanyInterviewArticles = async (companyId: number): Promise<CompanyInterviewArticleListType[]> => {
    const url = `${backendBasePath}/admin/companies/${companyId}/interview_articles`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: authHeaders,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { interview_articles: [] };
      });
    return result?.interview_articles ?? [];
  };

  const postCompanyInterviewArticle = async (companyId: number, formData: FormData) => {
    const url = `${backendBasePath}/admin/companies/${companyId}/interview_articles`;
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: authHeaders,
      body: formData,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
  };

  const getCompanyInterviewArticleDetail = async (
    interviewArticleId: number,
  ): Promise<GetCompanyInterviewArticleDetailType> => {
    const url = `${backendBasePath}/admin/interview_articles/${interviewArticleId}`;
    return fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: authHeaders,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
  };

  const updateCompanyInterviewArticle = async (interviewArticleId: number, formData: FormData) => {
    const url = `${backendBasePath}/admin/interview_articles/${interviewArticleId}`;
    return fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: authHeaders,
      body: formData,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
  };

  const deleteCompanyInterviewArticle = async (interviewArticleId: number) => {
    const url = `${backendBasePath}/admin/interview_articles/${interviewArticleId}`;
    return fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: authHeaders,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
  };

  const syncCompanyInterviewArticle = async (interviewArticleId: number) => {
    const url = `${backendBasePath}/admin/interview_articles/${interviewArticleId}/sync`;
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: authHeaders,
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
  };

  return {
    getCompanyInterviewArticles,
    postCompanyInterviewArticle,
    getCompanyInterviewArticleDetail,
    updateCompanyInterviewArticle,
    deleteCompanyInterviewArticle,
    syncCompanyInterviewArticle,
  };
};
